/**
 * Export Template Button Component
 *
 * Dropdown button for downloading infrastructure import templates in CSV or JSON format.
 * Designed to be placed alongside the Import dropdown in the Infrastructure toolbar.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfrastructureTemplates } from '../../hooks'
import type { TemplateFormat } from '../../types/infrastructure'
import {
  DownloadOptions,
  ExportErrorBanner,
  ExportTrigger,
  IncludeExamplesToggle,
} from './export-template-button/export-template-button.parts'

interface ExportTemplateButtonProps {
  caseId: string
  disabled?: boolean
  className?: string
}

export function ExportTemplateButton({
  caseId,
  disabled = false,
  className = '',
}: ExportTemplateButtonProps): ReactElement {
  const { t } = useTranslation('cases')
  const [showDropdown, setShowDropdown] = useState(false)
  const [includeExamples, setIncludeExamples] = useState(true)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { downloading, error, downloadTemplate, clearError } = useInfrastructureTemplates()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleDownload = async (format: TemplateFormat): Promise<void> => {
    clearError()
    const success = await downloadTemplate(caseId, format, { includeExamples })
    if (success) setShowDropdown(false)
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <ExportTrigger
        showDropdown={showDropdown}
        downloading={downloading}
        disabled={disabled}
        onToggle={() => {
          setShowDropdown(!showDropdown)
        }}
        t={t}
      />
      {showDropdown && (
        <div className="absolute top-full right-0 z-10 mt-1 w-56 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
          <IncludeExamplesToggle
            includeExamples={includeExamples}
            onToggle={() => {
              setIncludeExamples(!includeExamples)
            }}
            t={t}
          />
          <DownloadOptions
            downloading={downloading}
            onDownload={(format) => {
              void handleDownload(format)
            }}
            t={t}
          />
          <ExportErrorBanner error={error} />
        </div>
      )}
    </div>
  )
}

export default ExportTemplateButton
